import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
// styles
import styles from "./PayeeTable.module.scss";
// components
import Table from "../../common/Table";

// packages
import { useTranslation } from "react-i18next";

// types
import {
  ESenderRole,
  ETrasactionsType,
  IContractDetailsResponseData,
  UserProfile,
} from "../../../setup/api/services/contract/models";
// import ContractActions from "../contractActions";
import { useParams } from "react-router-dom";
import { ContractContext } from "../../../providers/contract";
import { MilestonePartyStatus } from "../partyStatus";
import useLocalStorage from "../../../hooks/useLocalStorage/useLocalStorage";
import { ECONTRACT_STATUS, EPARTY_STATUS } from "../../../enums";
import { IUser } from "../../../setup/api/services/auth/models";
import ContractActions from "../../../pages/contract/actions";
import Modal from "../../common/Modal";
import {
  PAYMENT_DETAILS,
  PAYMENT_PROOF_ATTACHMENT,
} from "../../../pages/contract/authenticatedContractDetails/constants";
import classNames from "classnames";
import moment from "moment";
import { getBankTransferDetails } from "../../../setup/api/services/payment";
import Gallery from "../gallery";
import PrimaryButton, { PrimaryButtonSize } from "../../common/PrimaryButton";
import { getUserDetailsById } from "../../../setup/api/services/user";
interface PayeeTableProps {
  partiesObj: Partial<UserProfile>;
  canMakeContractActions: (
    userInfo: Partial<UserProfile> | undefined
  ) => boolean | undefined;
  milestone: IContractDetailsResponseData;
}

export type RowData = {
  id: string;
  name: string;
  number: string;
  price: string;
  actions: () => any;
};

const PayeeTable: React.FC<PayeeTableProps> = ({
  partiesObj,
  milestone,
  canMakeContractActions,
}) => {
  const { t } = useTranslation();
  const { actions } = useContext(ContractContext);
  const { id } = useParams();
  const [rows, setRows] = useState<RowData[]>([]);
  const [userInfo] = useLocalStorage<IUser | null>("userInfo", null);
  const [images, setImages] = useState<string[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);

  const parties = useMemo(
    () => milestone?.partiesProfiles?.CUSTOMER,
    [milestone]
  );

  const refreshContractDetails = useCallback(() => {
    if (id) actions?.getContract();
  }, [id]);

  const closeModal = () => {
    setShowModal(false);
  };

  const getBankTransfer = useCallback(
    async (customerId) => {
      const userInfo = await getUserDetailsById(customerId);
      const referenceTransactionId = milestone?.transactions
        ? milestone?.transactions?.find(
            (transaction) =>
              transaction?.type === ETrasactionsType.MANUAL_BANK_TRANSFER &&
              (transaction?.fromUserId == String(userInfo.data.id) ||
                transaction?.fromUserId ==
                  String(userInfo.data.userIdentification))
          )?.referenceTransactionId
        : undefined;

      if (referenceTransactionId) {
        try {
          const data = await getBankTransferDetails(referenceTransactionId);
          if (data?.data?.docs) setImages(data?.data?.docs);
        } catch (error) {
          console.error("error", error);
        }
      }
    },
    [milestone]
  );

  const openModal = useCallback(
    (rowId) => {
      if (
        milestone?.parties?.some(
          (party) => party?.status === EPARTY_STATUS.PAYMENT_PENDING_APPROVAL
        )
      ) {
        getBankTransfer(rowId);
      }
      setShowModal(true);
    },
    [milestone, getBankTransfer]
  );

  const columns = [
    { type: "id", value: "" },
    { type: "name", value: `${t("lbl_party")}` },
    { type: "number", value: `${t("lbl_edit_profile_phone")}` },
    { type: "price", value: `${t("lbl_value")}` },
    { type: "status", value: `${t("lbl_condition")}` },
    { type: "action", value: "" },
  ];

  const transformPayToRows = useCallback(
    (parties): RowData[] => {
      parties?.forEach(function (party, index) {
        if (party?.phoneNumber === userInfo?.phoneNumber) {
          parties?.splice(index, 1);
          parties?.unshift(party);
        }
      });

      return parties?.map((item, index) => ({
        id: `${index + 1}.`,
        name: item?.profile?.fullName || t("lbl_not_registered_yet"),
        number: item?.phoneNumber,
        price: partiesObj[`${item?.phoneNumber}`]?.amount,
        status: partiesObj[`${item?.phoneNumber}`]?.status,
        action: "",
      }));
    },
    [partiesObj, t, userInfo?.phoneNumber]
  );

  useEffect(() => {
    if (parties?.length) {
      setRows(transformPayToRows(parties));
    }
  }, [parties, parties?.length, transformPayToRows]);

  const renderCell = useCallback(
    (column: string, row: unknown) => {
      const rowData = row as RowData;
      switch (column) {
        case "id":
          return (
            <p className={`${styles["p3"]} ${styles["g600"]}`}>{rowData?.id}</p>
          );
        case "name":
          return (
            <p className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.name}
            </p>
          );
        case "number":
          return (
            <p
              style={{ direction: "ltr" }}
              className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.number}
            </p>
          );
        case "price":
          return (
            <p className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.price}
            </p>
          );
        case "status":
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              {milestone?.status !==
                ECONTRACT_STATUS.CLOSED_RELEASE_PROVIDER && (
                <MilestonePartyStatus
                  partyStatus={
                    parties?.every(
                      (party) =>
                        partiesObj[`${party?.phoneNumber}`]?.status ===
                        EPARTY_STATUS.ITEM_ACCEPTED
                    )
                      ? t("lbl_money_transfer_phase")
                      : ""
                  }
                  statusColor={
                    parties?.every(
                      (party) =>
                        partiesObj[`${party?.phoneNumber}`]?.status ===
                        EPARTY_STATUS.ITEM_ACCEPTED
                    )
                      ? "#44C16E"
                      : ""
                  }
                  party={row as Partial<UserProfile>}
                />
              )}
              {partiesObj[`${rowData?.number}`]?.status ===
                EPARTY_STATUS.PAYMENT_PENDING_APPROVAL && (
                <>
                  <PrimaryButton
                    size={PrimaryButtonSize.SMALL}
                    width="142px"
                    onClick={openModal?.bind(
                      null,
                      partiesObj[`${rowData?.number}`]?.userId
                    )}>
                    {t("lbl_show_payment_details")}
                  </PrimaryButton>
                  <Modal
                    size={"small"}
                    visible={showModal}
                    onCancel={closeModal}>
                    <div
                      className={classNames(
                        styles["card"],
                        styles.bank_transfer_section
                      )}>
                      <div
                        className={classNames(
                          styles["column"],
                          styles["stretch"]
                        )}>
                        <h2>{PAYMENT_DETAILS}</h2>
                        <p style={{ marginBlock: "8px" }}>
                          {moment(
                            milestone?.transactions
                              ? milestone?.transactions?.find(
                                  (transaction) =>
                                    transaction?.type ===
                                      ETrasactionsType.MANUAL_BANK_TRANSFER &&
                                    transaction?.fromUserId ==
                                      partiesObj[`${rowData?.number}`]?.userId
                                )?.createdAt
                              : new Date()
                          )
                            .locale("ar")
                            .format(`DD MMMM YYYY - hh:mm`)}
                        </p>
                      </div>
                      <div className={styles["mt20"]}>
                        <p style={{ marginBlock: "24px" }}>
                          {PAYMENT_PROOF_ATTACHMENT}
                        </p>
                        {images.length > 0 && (
                          <Gallery
                            header={""}
                            images={images}
                            style={{ marginTop: "0.5rem" }}
                            isBrokerContract
                          />
                        )}
                      </div>
                    </div>
                  </Modal>
                </>
              )}
            </div>
          );
        case "action":
          return (
            <div style={{ padding: "8px" }}>
              {canMakeContractActions(
                parties?.find((party) => party?.phoneNumber === rowData?.number)
              ) && (
                <ContractActions
                  handleMenuClose={refreshContractDetails}
                  receiverType={ESenderRole.CUSTOMER}
                  contract={{
                    ...milestone,
                    senderId: milestone?.senderId || "-1",
                    receiverId: `${
                      parties?.find(
                        (party) => party?.phoneNumber === rowData?.number
                      )?.id
                    }`,
                  }}
                  id={"payee-table"}
                />
              )}
            </div>
          );
        default:
          return <p>{rowData[column]}</p>;
      }
    },
    [
      canMakeContractActions,
      images,
      milestone,
      openModal,
      parties,
      partiesObj,
      refreshContractDetails,
      showModal,
      t,
    ]
  );

  return <Table columns={columns} rows={rows} renderCell={renderCell} />;
};

export default React.memo(PayeeTable);
